/**
 * Typeahead component - single-select combobox with type-ahead filtering.
 *
 * The popup panel renders through a React portal into `document.body`,
 * positioned `fixed` from the trigger's `getBoundingClientRect()` (re-measured
 * on open, window resize and capture-phase scroll; opens upward when there's
 * no room below) — this is what keeps it from being clipped by an
 * `overflow-hidden` card or a dialog (task 064-05, D2). The panel's `width` is
 * likewise pinned to the trigger's measured `rect.width`, matching the
 * pre-portal `absolute left-0 right-0` behaviour (review F1) rather than
 * shrinking/growing to its content. Outside-click detection treats the root
 * *and* the portalled panel as "inside" (D3), since the panel is no longer a
 * DOM descendant of the root once portalled.
 *
 * Keyboard: Arrow Up/Down move a wrapping highlight through the rows; Enter
 * selects the highlighted row; Escape closes the popup, returns focus to the
 * trigger, and stops propagating (through the React tree — portals still
 * bubble synthetic events to their React ancestors); a printable key on the
 * focused, closed trigger opens the popup with that key as the query (D4).
 *
 * An optional `allOption` renders a first, always-visible row (e.g. "All
 * advertisers"); picking it calls `onChange(null)` (D5).
 *
 * Long option labels truncate (ellipsis) inside the fixed-width panel
 * (#130): the row's label/subtitle spans are cross-axis flex items of a
 * `flex-col items-start` row, so their width is content-based and a long
 * unbreakable token would widen them past the row — `max-w-full` caps the
 * span at the row width so `truncate` ellipsizes it. The row list pairs
 * `overflow-x-hidden` with its `overflow-y-auto` (which alone would compute
 * a horizontal scrollbar for any residual overflow), so vertical scrolling
 * stays the only scrolling, as the panel's width is pinned to the trigger.
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';

export interface TypeaheadProps<T> {
  options: T[];
  value: string | null;
  onChange: (id: string | null) => void;
  getLabel: (item: T) => string;
  getSubtitle?: (item: T) => string;
  getId: (item: T) => string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string[];
  /** A first, always-visible row (e.g. `{ label: 'All advertisers' }`); picking it calls `onChange(null)`. */
  allOption?: { label: string };
  /** Merged onto the component root — widths (`min-w-[170px]`, `w-64`) live here. */
  className?: string;
  /** Forwarded onto the trigger button, so a `<label htmlFor>` still resolves. */
  id?: string;
  'aria-label'?: string;
}

type PanelPosition = { left: number; width: number } & ({ top: number } | { bottom: number });

type Row<T> = { kind: 'all' } | { kind: 'option'; option: T };

export function Typeahead<T>({
  options,
  value,
  onChange,
  getLabel,
  getSubtitle,
  getId,
  placeholder = 'Select…',
  required = false,
  disabled = false,
  error,
  allOption,
  className,
  id,
  'aria-label': ariaLabel,
}: TypeaheadProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [highlight, setHighlight] = React.useState(0);
  const [position, setPosition] = React.useState<PanelPosition>({ left: 0, top: 0, width: 0 });
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const rowRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  // Re-measure the trigger (and, once mounted, the panel's own height) to
  // decide the fixed top/bottom anchor — downward by default, upward when
  // there isn't room below.
  const measure = React.useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < panelHeight && spaceAbove > spaceBelow;
    setPosition(
      openUpward
        ? { left: rect.left, bottom: window.innerHeight - rect.top + 4, width: rect.width }
        : { left: rect.left, top: rect.bottom + 4, width: rect.width }
    );
  }, []);

  React.useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  React.useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', measure);
    document.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      document.removeEventListener('scroll', measure, true);
    };
  }, [open, measure]);

  // Close on outside pointerdown while open — "inside" means the root (the
  // trigger + clear button) or the portalled panel.
  React.useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (!(e.target instanceof Node)) return;
      const insideRoot = rootRef.current?.contains(e.target) ?? false;
      const insidePanel = panelRef.current?.contains(e.target) ?? false;
      if (!insideRoot && !insidePanel) setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  // Highlight resets to the first row whenever the query changes.
  React.useEffect(() => {
    setHighlight(0);
  }, [q]);

  // Find the selected option by value
  const selectedOption = React.useMemo(() => {
    if (!value) return null;
    return options.find((o) => getId(o) === value) || null;
  }, [options, value, getId]);

  // Filter options based on search input — order is the caller's; filtering preserves it.
  const matches = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => {
      const label = getLabel(o);
      const subtitle = getSubtitle ? getSubtitle(o) : '';
      return label.toLowerCase().includes(needle) || subtitle.toLowerCase().includes(needle);
    });
  }, [options, q, getLabel, getSubtitle]);

  const rows: Array<Row<T>> = React.useMemo(() => {
    const base: Array<Row<T>> = allOption ? [{ kind: 'all' }] : [];
    return [...base, ...matches.map((option) => ({ kind: 'option' as const, option }))];
  }, [allOption, matches]);

  React.useEffect(() => {
    const el = highlight >= 0 && highlight < rows.length ? rowRefs.current[highlight] : null;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [highlight, rows.length]);

  const displayLabel = selectedOption
    ? getLabel(selectedOption)
    : allOption
      ? allOption.label
      : placeholder;
  const isPlaceholder = !selectedOption && !allOption;

  const openFrom = (rect: DOMRect) => {
    setPosition({ left: rect.left, top: rect.bottom + 4, width: rect.width });
    setOpen(true);
  };

  const handleSelect = (option: T) => {
    onChange(getId(option));
    setOpen(false);
    setQ('');
  };

  const handleSelectAll = () => {
    onChange(null);
    setOpen(false);
    setQ('');
  };

  const handleSelectRow = (row: Row<T>) => {
    if (row.kind === 'all') handleSelectAll();
    else handleSelect(row.option);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setQ('');
  };

  const handleTriggerClick = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) openFrom(rect);
    else setOpen(true);
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled || open) return;
    // A printable character on the focused, closed trigger opens the popup
    // with that character as the query. Space and Enter already activate the
    // native button (toggling it via onClick) — don't double-handle them.
    if (e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setPosition({ left: rect.left, top: rect.bottom + 4, width: rect.width });
      setQ(e.key);
      setOpen(true);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (rows.length > 0) setHighlight((h) => (h + 1) % rows.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (rows.length > 0) setHighlight((h) => (h - 1 + rows.length) % rows.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[highlight];
      if (row) handleSelectRow(row);
    }
  };

  return (
    <div ref={rootRef} className={cn('space-y-1', className)}>
      <div className="flex items-center gap-1">
        <div className="relative min-w-0 flex-1">
          <button
            ref={triggerRef}
            id={id}
            aria-label={ariaLabel}
            type="button"
            disabled={disabled}
            onClick={handleTriggerClick}
            onKeyDown={handleTriggerKeyDown}
            className={cn(
              'flex h-9 w-full items-center gap-2 rounded-lg bg-surface px-3 text-left text-xs font-semibold text-ink hairline transition',
              'hover:bg-line/40',
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              error && error.length > 0 && 'border-destructive'
            )}
          >
            <span className={cn('min-w-0 flex-1 truncate', isPlaceholder && 'text-faint')}>
              {displayLabel}
            </span>
            <svg
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              className={cn(
                'shrink-0 transition-transform',
                open ? 'rotate-180' : '',
                disabled ? 'opacity-50' : ''
              )}
            >
              <path
                d="M1 1L5 5L9 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {open &&
            createPortal(
              <div
                ref={panelRef}
                data-testid="typeahead-panel"
                style={{
                  position: 'fixed',
                  left: position.left,
                  width: position.width,
                  ...('top' in position ? { top: position.top } : { bottom: position.bottom }),
                }}
                className="z-[60] overflow-hidden rounded-lg bg-surface hairline shadow-lg"
              >
                <div className="border-b border-line p-2">
                  <input
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    placeholder="Type to filter…"
                    className="w-full rounded-md bg-paper px-3 py-2 text-xs text-ink outline-none placeholder:text-faint"
                  />
                </div>
                <div className="max-h-56 overflow-x-hidden overflow-y-auto">
                  {rows.map((row, i) => {
                    if (row.kind === 'all') {
                      const isSelected = !selectedOption;
                      return (
                        <button
                          key="__all__"
                          ref={(el) => {
                            rowRefs.current[i] = el;
                          }}
                          type="button"
                          onClick={() => handleSelectAll()}
                          className={cn(
                            'flex w-full flex-col items-start gap-0.5 border-b border-line/60 px-3 py-2 text-left text-xs transition last:border-b-0',
                            isSelected
                              ? 'bg-accent-soft/60 text-accent'
                              : i === highlight
                                ? 'bg-paper text-ink'
                                : 'text-ink hover:bg-paper'
                          )}
                        >
                          <span className="max-w-full truncate font-semibold">
                            {allOption?.label}
                          </span>
                        </button>
                      );
                    }
                    const o = row.option;
                    const isSelected = selectedOption != null && getId(o) === getId(selectedOption);
                    return (
                      <button
                        key={getId(o)}
                        ref={(el) => {
                          rowRefs.current[i] = el;
                        }}
                        type="button"
                        onClick={() => handleSelect(o)}
                        className={cn(
                          'flex w-full flex-col items-start gap-0.5 border-b border-line/60 px-3 py-2 text-left text-xs transition last:border-b-0',
                          isSelected
                            ? 'bg-accent-soft/60 text-accent'
                            : i === highlight
                              ? 'bg-paper text-ink'
                              : 'text-ink hover:bg-paper'
                        )}
                      >
                        <span className="max-w-full truncate font-semibold">{getLabel(o)}</span>
                        {getSubtitle && (
                          <span className="max-w-full truncate text-xs text-muted">
                            {getSubtitle(o)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {matches.length === 0 && (
                    <div className="px-3 py-4 text-center text-xs text-faint">No match</div>
                  )}
                </div>
              </div>,
              document.body
            )}
        </div>
        {selectedOption && (
          <button
            type="button"
            onClick={handleClear}
            className="flex h-9 w-8 items-center justify-center rounded-lg bg-surface text-faint hairline transition hover:text-rose hover:bg-line/40"
            disabled={disabled}
            title="Clear selection"
          >
            ×
          </button>
        )}
      </div>

      {required && !selectedOption && <span className="text-xs text-rose">*</span>}

      {error && error.length > 0 && (
        <p className="mt-1 text-sm text-destructive" role="alert">
          {error.join(', ')}
        </p>
      )}
    </div>
  );
}
